import classNames from 'classnames';
import { composeRefs } from 'radix-ui/internal';
import * as React from 'react';
import { useThrottledCallback } from 'use-debounce';
export type FadedBottomScrollProps = React.PropsWithChildren<{
  className?: string;
  bodyClassName?: string;
}>;
export const FadedBottomScroll = React.forwardRef<HTMLDivElement, FadedBottomScrollProps>(({
  children,
  className,
  bodyClassName
}, forwardedRef) => {
  const {
    scrollRef,
    fadeOpacity
  } = useScrollBottomFade();
  return <div ref={composeRefs(forwardedRef, scrollRef)} className={classNames('FadedBottomScroll', className)} style={
  // Introducing lint rule banning type assertions
  // Type assertion is here to workaround --scroll-panel-bottom-fade-opacity being unknown property
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  {
    '--scroll-panel-bottom-fade-opacity': fadeOpacity
  } as React.CSSProperties}>
      <div className={classNames('FadedBottomScrollBody', bodyClassName)}>
        {children}
      </div>
    </div>;
});
FadedBottomScroll.displayName = 'FadedBottomScroll';
const useScrollBottomFade = () => {
  const [scrollElement, setScrollElement] = React.useState<HTMLDivElement | null>(null);
  const scrollElementCallbackRef = React.useCallback((node: HTMLDivElement | null) => {
    setScrollElement(node);
  }, []);

  // Track Scroll Position

  const [fadeOpacity, setFadeOpacity] = React.useState(0);
  const updateFadeOpacity = React.useCallback(() => {
    if (scrollElement) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = scrollElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const nextFadeOpacity = distanceFromBottom < 200 ? distanceFromBottom / 200 : 1;
      setFadeOpacity(nextFadeOpacity);
    }
  }, [scrollElement]);
  const debouncedHandleScroll = useThrottledCallback(() => {
    updateFadeOpacity();
  }, 20);
  React.useEffect(() => {
    const element = scrollElement;
    updateFadeOpacity();
    if (element) {
      element.addEventListener('scroll', debouncedHandleScroll);
      debouncedHandleScroll(); // Initial calculation
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', debouncedHandleScroll);
      }
    };
  }, [debouncedHandleScroll, scrollElement, updateFadeOpacity]);

  // Track body resizes;
  React.useEffect(() => {
    const observed = scrollElement?.querySelector<HTMLElement>('.FadedBottomScrollBody');
    if (!observed) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      updateFadeOpacity();
    });

    // Observe the body element so we can update the fade opacity when the
    // content changes size as well as the scrollbar width.
    resizeObserver.observe(observed);
    return () => resizeObserver.unobserve(observed);
  }, [scrollElement, updateFadeOpacity]);
  return {
    scrollRef: scrollElementCallbackRef,
    fadeOpacity
  };
};